<template>
    <div>
        <div class="loader" v-if="loading"></div>
        <div v-else>
            <div v-if="canWrite && !isLockedForEdit"
                class="alert alert-info alert-dismissible fade show" role="alert"
            >
                <ul class="ml-2">
                    <li>{{ $t('registers.events.evaluation.tempRanking.activeButtonInfo1')}}</li>
                    <li>{{ $t('registers.events.evaluation.tempRanking.activeButtonInfo')}}</li>
                    <li>{{ $t('registers.events.evaluation.tempRanking.activeButtonInfo2')}}</li>
                    <li>{{ $t('registers.events.evaluation.tempRanking.activeButtonInfo3')}}</li>
                </ul>

                <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>

            <div class="btn-group">
                <div v-if="canWrite && !isLockedForEdit">
                    <button class="btn btn-primary"
                            @click.stop="createTempRanking">
                        <i class="fas fa-plus"></i>
                        {{ $t('registers.events.evaluation.tempRanking.new')}}
                    </button>
                    <button class="btn btn-primary"
                            @click.stop="addCandidate">
                        <i class="far fa-user"></i>
                        {{ $t('registers.events.evaluation.tempRanking.addCandidate')}}
                    </button>
                    <button v-if="tempRanking && tempRanking.id && tempRanking.isActive" class="btn btn-danger"
                            :disabled="deactivating"
                            @click.stop="deactivateTempRanking">
                        <i class="fas fa-ban"></i>
                        {{ $t('buttons.deactivate.btnText')}}
                    </button>
                    <button v-if="tempRanking && tempRanking.id && !tempRanking.isActive" class="btn btn-success"
                            :disabled="activating"
                            @click.stop="activateTempRanking">
                        <i class="fas fa-check"></i>
                        {{ $t('buttons.activate.btnText')}}
                    </button>
                </div>
                <div v-if="hasEditRights && tempRanking && tempRanking.id">
                    <button class="btn btn-default"
                            :disabled="syncing"
                            @click.stop="syncAllWithFormEntityDataBtnClick">
                        <i v-if="syncing" class="fas fa-spinner fa-spin"></i>
                        <i v-else class="fas fa-sync"></i>
                        {{ $t('buttons.syncWithFormEntityData.btnText')}}
                    </button>
                </div>
                <div v-if="loadedValidTempRanking">
                    <button class="btn btn-info"
                            @click.stop="tempRankingHistory">
                        <i class="fas fa-history"></i>
                        {{ $t('buttons.history.btnText')}}
                    </button>
                </div>
            </div>

            <div v-if="loadedValidTempRanking">
                <TempRankingEditor :tempRanking="tempRanking" :editing="editing">
                    <template v-slot:statistics>
                        <ExpandableDetails class="m-0" v-if="tempRanking"
                                    :title="$t('registers.events.evaluation.tempRanking.statistica')"
                                    :showInCard="true"
                                    :expanded="true">
                            <template v-slot:content>
                                <Statistics :tempRankingId="tempRanking.id"
                                            ref="statistics"></Statistics>
                            </template>
                        </ExpandableDetails>
                    </template>

                    <template v-slot:actions>
                        <div class="btn-group" v-if="!isLockedForEdit">
                            <button v-if="!editing" class="btn btn-primary"
                                    @click.stop="edit">
                                <i class="fas fa-edit"></i>
                                {{ $t('buttons.edit.btnText')}}
                            </button>
                            <button v-if="editing" class="btn btn-danger"
                                    :disabled="saving"
                                    @click.stop="cancel">
                                <i class="fas fa-ban"></i>
                                {{ $t('buttons.cancel')}}
                            </button>
                            <button v-if="editing" class="btn btn-success"
                                    :disabled="saving"
                                    @click.stop="save">
                                <i class="fas fa-save"></i>
                                {{ $t('buttons.save.btnText')}}
                            </button>
                        </div>
                    </template>
                </TempRankingEditor>
            </div>
            <div v-else>
                <div class="card mb-5">
                    <div class="card-body">
                        <h4>{{ $t('registers.events.evaluation.tempRanking.notCreated')}}</h4>
                    </div>
                </div>
            </div>
        </div>

        <CreateModal :show="showCreate" @onCreate="onCreate" @onClose="onCreateClose"></CreateModal>
    </div>
</template>

<script>
    import { EventClaims } from '../../../models/enums';
    import { TempRanking } from '../../../models/models';
    import ExpandableDetails from '../../../components/common/ExpandableDetails';
    import TempRankingEditor from '@/components/dynamic/ranking/TempRankingEditor';
    import tempRankingService from '../../../services/tempRanking.service';
    import CreateModal from './create.modal';
    import Statistics from './Statistics';

    import { mapGetters } from 'vuex';

    export default {
        inject: ['$validator'],
        props: {
            id: {
                type: Number,
                default: 0,
            },
            eventId: {
                type: Number,
                default: 0,
            },
            details: {
                type: Boolean,
                default: false
            },
            canWrite: {
                type: Boolean,
                default: false
            },
            isLockedForEdit: {
                type: Boolean,
                default: false
            }
        },
        components: {
            TempRankingEditor,
            ExpandableDetails,
            CreateModal,
            Statistics
        },
        data() {
            return {
                loading: false,
                activating: false,
                deactivating: false,
                tempRanking: {
                    type: TempRanking,
                    default: null
                },
                editing: false,
                saving: false,
                origValue: {
                    type: TempRanking,
                    default: null
                },
                participantsCount: 0,
                componentTitle: this.$t('registers.events.evaluation.tempRanking.title'),
                unauthorized: false,
                showCreate: false,
                syncing: false
            }
        },
        methods: {
            load() {
                this.loading = true;

                tempRankingService
                    .load(this.id)
                    .then(result => {
                        this.loading = false;

                        if (result.data.tempRanking) {
                            this.tempRanking = new TempRanking(result.data.tempRanking);
                        }
                        this.loading = false;

                        let statusCode = this.$helpers.responseStatusCode(result);
                        if (statusCode === 403) {
                            this.accessDenied();
                        }
                    });
            },
            setAdmitted(count) {
                if (this.tempRanking && !isNaN(count)) {
                    this.tempRanking.admittedCount = count;
                }
            },
            setReserve(count) {
                if (this.tempRanking && !isNaN(count)) {
                    this.tempRanking.reserveCount = count;
                }
            },
            setRejected(count) {
                if (this.tempRanking && !isNaN(count)) {
                    this.tempRanking.rejectedCount = count;
                }
            },
            setParticipants(count) {
                if (this.participants && !isNaN(count)) {
                    this.participants = count;
                }
            },
            edit() {
                this.updateOrgiValue(this.tempRanking);
                this.editing = true;
            },
            cancel() {
                if (!this.tempRanking) {
                    this.tempRanking = new TempRanking(this.origValue);
                } else {
                    this.updateModel(this.origValue);
                }

                this.editing = false;
            },
            updateModel(m) {
                this.tempRanking.id = m.id;
                this.tempRanking.registerEntityId = m.registerEntityId;
                this.tempRanking.name = m.name;
                this.tempRanking.eventName = m.eventName;
                this.tempRanking.studentsMaxNum = m.studentsMaxNum;
                this.tempRanking.isValid = m.isValid;
                this.tempRanking.accommodationRequestRequired = m.accommodationRequestRequired;
                this.tempRanking.ver = m.ver;
                this.tempRanking.creatorUsername = m.creatorUsername;
                this.tempRanking.createdAt = m.createdAt;
                this.tempRanking.updaterUsername = m.updaterUsername;
                this.tempRanking.updatedAt = m.updatedAt;
                this.tempRanking.confirmMaxDate = m.confirmMaxDate;
                this.tempRanking.admittedCount = m.admittedCount;
                this.tempRanking.reserveCount = m.reserveCount;
                this.tempRanking.rejectedCount = m.rejectedCount;
            },
            updateOrgiValue(m) {
                if (!this.origValue) {
                    this.origValue = new TempRanking();
                }

                this.origValue.id = m.id;
                this.origValue.registerEntityId = m.registerEntityId;
                this.origValue.name = m.name;
                this.origValue.eventName = m.eventName;
                this.origValue.studentsMaxNum = m.studentsMaxNum;
                this.origValue.isValid = m.isValid;
                this.origValue.accommodationRequestRequired = m.accommodationRequestRequired;
                this.origValue.ver = m.ver;
                this.origValue.creatorUsername = m.creatorUsername;
                this.origValue.createdAt = m.createdAt;
                this.origValue.updaterUsername = m.updaterUsername;
                this.origValue.updatedAt = m.updatedAt;
                this.origValue.confirmMaxDate = m.confirmMaxDate;
                this.origValue.admittedCount = m.admittedCount;
                this.origValue.reserveCount = m.reserveCount;
                this.origValue.rejectedCount = m.rejectedCount;
            },
            save() {
                if (this.isNaN) {
                    this.create();
                } else {
                    this.update();
                }
            },
            create() {
                this.saving = true

                tempRankingService
                    .create(this.tempRanking)
                    .then((response => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });

                        this.saving = false
                        this.editing = false

                        this.$emit("save");
                    })).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });

                        this.saving = false
                        this.editing = false
                    });
            },
            update() {
                this.saving = true

                tempRankingService
                    .update(this.tempRanking)
                    .then((response => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });

                        this.saving = false
                        this.editing = false
                        this.$emit("save")
                    })).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });


                        this.saving = false
                        this.editing = false
                    });
            },
            getValidParticipants() {
                this.$http.get(`/api/participant/countValidForEvent/${this.tempRanking.registerEntityId}`).then(response => {
                    let val = Number(response.data.count);
                    if (!isNaN(val)) {
                        this.participantsCount = val;
                    }
                }).catch((error) => {
                });
            },
            createTempRanking() {
                kendo.confirm(this.$t('registers.events.evaluation.tempRanking.createNewConfirmation'))
                    .then(function () {
                        this.loading = true;

                        let eventId = this.tempRanking.registerEntityId;
                        if (!eventId) {
                            eventId = this.eventId;
                        }

                        tempRankingService
                            .rank(eventId)
                            .then((response => {
                                this.loading = false;
                                var newTempRankId = Number(response.data.tempRankingId);
                                if (!isNaN(newTempRankId)) {
                                    this.id = newTempRankId;
                                }
                                this.reload();
                                this.$emit("save");
                            })).catch(error => {
                                this.loading = false;
                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                    text: this.$helpers.errorText(error),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                });
                            });
                    }.bind(this));
            },
            reload() {
                this.$emit("tempRankingReloaded", this.id);
                this.load();
                this.getValidParticipants();
                this.refreshStats();
            },
            refreshStats() {

            },
            activateTempRanking() {
                this.activating = true;

                tempRankingService
                    .activate(this.tempRanking)
                    .then((response => {
                        this.activating = false;
                        this.reload();
                    })).catch(error => {
                        this.activating = false;
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            deactivateTempRanking() {
                this.deactivating = true;

                tempRankingService
                    .deactivate(this.tempRanking)
                    .then((response => {
                        this.deactivating = false;
                        this.reload();
                    })).catch(error => {
                        this.deactivating = false;
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            accessDenied() {
                this.unauthorized = true;
            },
            onCreateClose() {
                this.showCreate = false;
            },
            onCreate() {
                this.reload();
            },
            reloadStatistics() {
                const statistics = this.$refs.statistics;
                if (statistics) {
                    statistics.reload();
                }
            },
            addCandidate() {
                this.showCreate = true;
            },
            tempRankingHistory() {
                let routeData = this.$router.resolve({
                    name: 'tempRankingHistory',
                    query: {
                        eventId: this.eventId
                    }
                })
                window.open(routeData.href, '_blank')
            },
            syncAllWithFormEntityDataBtnClick() {
                if (!this.tempRanking.id) {
                    return;
                }

                kendo
                    .confirm(this.$t('registers.events.evaluation.tempRanking.confirmation.syncAllWithFormEntityData'))
                    .then(
                        function () {
                            this.syncing = true;

                            tempRankingService
                                .syncAllWithFormEntityData(this.tempRanking.id)
                                .then((response) => {
                                    this.$emit("syncWithFormEntityData");

                                    this.$vs.notify({
                                        title: this.$t("buttons.syncWithFormEntityData.btnText"),
                                        text: this.$helpers.responseMessage(response),
                                        color: "success",
                                        time: this.$store.getters
                                            .notificationDuration,
                                    });
                                })
                                .catch((error) => {
                                    this.$vs.notify({
                                        title: this.$t("buttons.syncWithFormEntityData.btnText"),
                                        text: this.$helpers.errorText(error),
                                        color: this.$helpers.errorColor(error),
                                        time: this.$store.getters.notificationDuration,
                                    });
                                })
                                .then(() => { this.syncing = false; });
                        }.bind(this)
                    );
            }
        },
        mounted() {
            this.load();
            this.getValidParticipants();
        },
        computed: {
            ...mapGetters(['isEnVersion', 'eventClaims']),
            isNew() {
                return isNaN(this.id) || this.id === undefined || this.id === 0;
            },
            hasEditRights() {
                if (!this.isLockedForEdit) {
                    const claim = this.eventClaims[EventClaims.TempRanking];
                    return claim.create || claim.update;
                } else {
                    return false;
                }
            },
            freeSeats() {
                return this.tempRanking.studentsMaxNum - this.participantsCount - (this.tempRanking.admittedCount || 0);
            },
            loadedValidTempRanking() {
                return this.tempRanking && !isNaN(this.tempRanking.id) && (this.tempRanking.id !== undefined || this.tempRanking.id > 0);
            }
        }
    }
</script>
